import React, { createContext, useContext, useState, useEffect } from 'react';
import { API_BASE_URL } from '../config/api';
import { apiClient, setLoadingFunction } from '../utils/apiClient';
import { clearStoredSession, loadStoredSession, saveStoredSession, SESSION_DURATION_MS, STAY_SIGNED_IN_DURATION_MS } from '../utils/authSession';
import { observeSessionFailures } from '../utils/sessionFailures';

const AppContext = createContext();

export const useApp = () => useContext(AppContext);

export const AppProvider = ({ children }) => {
  const [instructors, setInstructors] = useState([]);
  const [instructorError, setInstructorError] = useState('');
  const [roles, setRoles] = useState([]);
  const [session, setSession] = useState(loadStoredSession);
  const sessionRef = React.useRef(session);
  const currentUser = session?.user || null;
  const setCurrentUser = React.useCallback((update, { staySignedIn = false } = {}) => {
    const previous = sessionRef.current;
    const user = typeof update === 'function' ? update(previous?.user || null) : update;
    if (!user?.sessionId) {
      clearStoredSession();
      sessionRef.current = null;
      setSession(null);
      return;
    }
    const expiresAt = previous?.user?.sessionId === user.sessionId && previous.expiresAt > Date.now()
      ? previous.expiresAt : Date.now() + (staySignedIn ? STAY_SIGNED_IN_DURATION_MS : SESSION_DURATION_MS);
    const next = { user, expiresAt };
    saveStoredSession(user, expiresAt);
    sessionRef.current = next;
    setSession(next);
  }, []);
  const [currentOrg, setCurrentOrg] = useState(null);
  const [reservationTypes, setReservationTypes] = useState([]);
  const [reservationTypesError, setReservationTypesError] = useState('');
  const [loadingReservationTypes, setLoadingReservationTypes] = useState(false);
  const reservationTypeRequest = React.useRef(0);
  const fetchReservationTypes = React.useCallback(async () => {
    const request = ++reservationTypeRequest.current;
    if (!currentUser?.sessionId) return;
    setLoadingReservationTypes(true);
    setReservationTypesError('');
    try {
      const response = await apiClient('/reservation-type/all', { headers: { 'session-id': currentUser.sessionId } });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.error || 'Unable to load reservation types.');
      if (!Array.isArray(data)) throw new Error('Invalid reservation types response.');
      if (request === reservationTypeRequest.current) setReservationTypes(data);
    } catch (error) {
      if (request === reservationTypeRequest.current) { setReservationTypes([]); setReservationTypesError(error.message); }
    } finally {
      if (request === reservationTypeRequest.current) setLoadingReservationTypes(false);
    }
  }, [currentUser?.sessionId]);
  useEffect(() => {
    setReservationTypes([]);
    setReservationTypesError('');
    setLoadingReservationTypes(false);
    fetchReservationTypes();
    return () => { reservationTypeRequest.current += 1; };
  }, [fetchReservationTypes, currentUser?.user?.orgId]);
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [locationError, setLocationError] = useState('');
  const [locationResources, setLocationResources] = useState({ instructors: [], aircraft: [] });
  const [locationWorkSchedules, setLocationWorkSchedules] = useState({});
  const [locationAircraftSchedules, setLocationAircraftSchedules] = useState({});
  const [locationAircraftSchedulesError, setLocationAircraftSchedulesError] = useState('');
  const [loadingLocationAircraftSchedules, setLoadingLocationAircraftSchedules] = useState(false);
  const aircraftScheduleRequest = React.useRef(0);
  const fetchLocationAircraftSchedules = React.useCallback(async () => {
    const request = ++aircraftScheduleRequest.current;
    if (!currentUser?.sessionId || !locations.length) return;
    setLoadingLocationAircraftSchedules(true);
    setLocationAircraftSchedulesError('');
    try {
      const records = await Promise.all(locations.map(async location => {
        const response = await apiClient(`/availability-schedule/all?locationId=${encodeURIComponent(location.id)}`, { headers: { 'session-id': currentUser.sessionId } });
        const data = await response.json();
        if (!response.ok) throw new Error(data?.error || 'Unable to load aircraft location schedules.');
        if (!Array.isArray(data)) throw new Error('Invalid aircraft location schedules response.');
        return [String(location.id), data.map(schedule => ({ ...schedule, locationId: schedule.locationId ?? location.id }))];
      }));
      if (request === aircraftScheduleRequest.current) setLocationAircraftSchedules(Object.fromEntries(records));
    } catch (error) {
      if (request === aircraftScheduleRequest.current) {
        setLocationAircraftSchedules({});
        setLocationAircraftSchedulesError(error.message);
      }
    } finally {
      if (request === aircraftScheduleRequest.current) setLoadingLocationAircraftSchedules(false);
    }
  }, [currentUser?.sessionId, currentUser?.user?.orgId, locations]);
  useEffect(() => {
    setLocationAircraftSchedules({});
    setLocationAircraftSchedulesError('');
    setLoadingLocationAircraftSchedules(false);
    fetchLocationAircraftSchedules();
    return () => { aircraftScheduleRequest.current += 1; };
  }, [fetchLocationAircraftSchedules]);
  const updateLocationAircraftSchedules = React.useCallback(records => {
    setLocationAircraftSchedules(previous => {
      const next = { ...previous };
      for (const record of records) {
        const key = String(record.locationId);
        const list = next[key] || [];
        const existing = list.find(item => Number(item.aircraftId) === Number(record.aircraftId));
        next[key] = list.filter(item => Number(item.aircraftId) !== Number(record.aircraftId));
        if (!record.deleted) next[key].push({ ...existing, ...record });
      }
      return next;
    });
  }, []);
  const [locationWorkSchedulesError, setLocationWorkSchedulesError] = useState('');
  const [loadingLocationWorkSchedules, setLoadingLocationWorkSchedules] = useState(false);
  const workScheduleRequest = React.useRef(0);
  const fetchLocationWorkSchedules = React.useCallback(async () => {
    const request = ++workScheduleRequest.current;
    if (!currentUser?.sessionId || !locations.length) return;
    setLoadingLocationWorkSchedules(true);
    setLocationWorkSchedulesError('');
    try {
      const records = await Promise.all(locations.map(async location => {
        const response = await apiClient(`/work-schedule/all?locationId=${encodeURIComponent(location.id)}`, { headers: { 'session-id': currentUser.sessionId } });
        const data = await response.json();
        if (!response.ok) throw new Error(data?.error || 'Unable to load location schedules.');
        if (!Array.isArray(data)) throw new Error('Invalid location schedules response.');
        return [String(location.id), data.map(schedule => ({ ...schedule, locationId: schedule.locationId ?? location.id }))];
      }));
      if (request === workScheduleRequest.current) setLocationWorkSchedules(Object.fromEntries(records));
    } catch (error) {
      if (request === workScheduleRequest.current) { setLocationWorkSchedules({}); setLocationWorkSchedulesError(error.message); }
    } finally {
      if (request === workScheduleRequest.current) setLoadingLocationWorkSchedules(false);
    }
  }, [currentUser?.sessionId, currentUser?.user?.orgId, locations]);
  useEffect(() => {
    setLocationWorkSchedules({});
    setLocationWorkSchedulesError('');
    setLoadingLocationWorkSchedules(false);
    fetchLocationWorkSchedules();
    return () => { workScheduleRequest.current += 1; };
  }, [fetchLocationWorkSchedules]);
  const updateLocationWorkSchedules = React.useCallback(records => {
    setLocationWorkSchedules(previous => {
      const next = { ...previous };
      for (const record of records) {
        const key = String(record.locationId);
        const list = next[key] || [];
        const existing = list.find(item => Number(item.userId) === Number(record.userId));
        next[key] = list.filter(item => Number(item.userId) !== Number(record.userId));
        if (!record.deleted) next[key].push({ ...existing, ...record });
      }
      return next;
    });
  }, []);
  const resourceRequest = React.useRef(0);
  const locationRequest = React.useRef(0);
  const fetchLocations = React.useCallback(async () => {
    const requestId = ++locationRequest.current;
    if (!currentUser?.sessionId) return;
    try {
      const response = await fetch(`${API_BASE_URL}/location/all`, { headers: { 'session-id': currentUser.sessionId } });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Unable to load locations');
      if (locationRequest.current !== requestId) return;
      const list = Array.isArray(data) ? data : [];
      setLocations(list);
      setLocationError('');
      setSelectedLocationId(previous => list.some(item => String(item.id) === String(previous))
        ? previous : String((list.find(item => item.isDefault) || list[0])?.id || ''));
    } catch (error) {
      if (locationRequest.current !== requestId) return;
      setLocations([]);
      setSelectedLocationId('');
      setLocationError(error.message);
    }
  }, [currentUser?.sessionId]);
  useEffect(() => {
    setLocations([]);
    setSelectedLocationId('');
    setLocationResources({ instructors: [], aircraft: [] });
    setLocationError('');
    fetchLocations();
    return () => { locationRequest.current += 1; };
  }, [fetchLocations, currentUser?.user?.orgId]);
  const refreshLocationResources = React.useCallback(async () => {
    const requestId = ++resourceRequest.current;
    if (!currentUser?.sessionId || !selectedLocationId) return;
    const headers = { 'session-id': currentUser.sessionId };
    try {
      const data = await Promise.all(['/instructors', '/aircraft/all'].map(async endpoint => {
        const response = await fetch(`${API_BASE_URL}${endpoint}?locationId=${encodeURIComponent(selectedLocationId)}`, { headers });
        const result = await response.json();
        if (!response.ok) throw new Error(result.error || 'Unable to load location resources');
        return Array.isArray(result) ? result : [];
      }));
      if (resourceRequest.current !== requestId) return;
      setLocationResources({ locationId: selectedLocationId, instructors: data[0], aircraft: data[1] });
      setLocationError('');
    } catch (error) {
      if (resourceRequest.current !== requestId) return;
      setLocationResources({ instructors: [], aircraft: [] });
      setLocationError(error.message);
    }
  }, [currentUser?.sessionId, selectedLocationId]);
  useEffect(() => {
    setLocationResources({ instructors: [], aircraft: [] });
    refreshLocationResources();
    return () => { resourceRequest.current += 1; };
  }, [refreshLocationResources]);
  const [isLoading, setIsLoading] = useState(false);
  const [isPopupQuestionLoading, setIsPopupQuestionLoading] = useState(false);
  
  useEffect(() => {
    setLoadingFunction(setIsLoading);
  }, []);

  useEffect(() => {
    setCurrentOrg(null);
    if (!currentUser?.sessionId || !currentUser?.user?.orgId) return;
    let active = true;
    let controller;
    const headers = { 'session-id': currentUser.sessionId };
    const refresh = () => {
    controller?.abort();
    controller = new AbortController();
    const signal = controller.signal;
    fetch(`${API_BASE_URL}/organization/${currentUser.user.orgId}`, { headers, signal, cache: 'no-store' })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        return response.json();
      })
      .then(data => {
        if (active && !signal.aborted) setCurrentOrg(data);
      })
      .catch(error => {
        if (!active || signal.aborted) return;
        console.error('Unable to load current organization:', error);
        setCurrentOrg(null);
      });
    };
    refresh();
    window.addEventListener('focus', refresh);
    window.addEventListener('organization-updated', refresh);
    return () => {
      active = false;
      controller?.abort();
      window.removeEventListener('focus', refresh);
      window.removeEventListener('organization-updated', refresh);
    };
  }, [currentUser?.sessionId, currentUser?.user?.orgId]);

  useEffect(() => {
    if (!currentUser?.sessionId) return;
    const headers = { 'session-id': currentUser.sessionId };
    fetch(`${API_BASE_URL}/instructors`, { headers })
      .then(async response => {
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data?.error || `HTTP ${response.status}: ${response.statusText}`);
        }
        if (!Array.isArray(data)) throw new Error('Invalid instructor list response');
        return data;
      })
      .then(data => {
        setInstructors(data);
        setInstructorError('');
      })
      .catch(error => {
        setInstructors([]);
        setInstructorError(error.message);
      });
  }, [currentUser?.sessionId]);

  useEffect(() => {
    if (!currentUser?.sessionId) return;
    const headers = { 'session-id': currentUser.sessionId };
    fetch(`${API_BASE_URL}/role/list`, { headers })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        return response.json();
      })
      .then(data => {
        setRoles(Array.isArray(data) ? data : []);
      })
      .catch(error => {
        setRoles([]);
      });
  }, [currentUser?.sessionId]);
  const [people, setPeople] = useState([]);

  useEffect(() => {
    if (!currentUser?.sessionId) return;
    const headers = { 'session-id': currentUser.sessionId };
    fetch(`${API_BASE_URL}/user/all`, { headers })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        return response.json();
      })
      .then(data => {
        setPeople(Array.isArray(data) ? data : []);
      })
      .catch(error => {
        setPeople([]);
      });
  }, [currentUser?.sessionId]);
  const [aircraft, setAircraft] = useState([]);
  const [ownedAircraft, setOwnedAircraft] = useState([]);

  useEffect(() => {
    if (!currentUser?.sessionId) return;
    const headers = { 'session-id': currentUser.sessionId };
    fetch(`${API_BASE_URL}/aircraft/all`, { headers })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        return response.json();
      })
      .then(data => {
        setAircraft(Array.isArray(data) ? data : []);
      })
      .catch(error => {
        setAircraft([]);
      });
  }, [currentUser?.sessionId]);

  useEffect(() => {
    if (!currentUser?.sessionId || !currentUser?.user?.id) {
      setOwnedAircraft([]);
      return;
    }
    const headers = { 'session-id': currentUser.sessionId };
    fetch(`${API_BASE_URL}/aircraft/owner/${currentUser.user.id}`, { headers })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        return response.json();
      })
      .then(data => {
        setOwnedAircraft(Array.isArray(data) ? data : []);
      })
      .catch(error => {
        setOwnedAircraft([]);
      });
  }, [currentUser?.sessionId, currentUser?.user?.orgRole, currentUser?.user?.id]);
  
  const [maneuvers, setManeuvers] = useState([]);
  const [settings, setSettings] = useState({
    logoImage: '',
    website: '',
    airports: '',
    cautionWind: 15,
    maxWind: 25,
    cautionCeiling: 1000,
    minCeiling: 500,
    minVisibility: 3,
    cautionVisibility: 5,
    flightReservationDayStart: 6,
    flightReservationDayEnd: 22,
    simRate: 40,
    instructionOwnerAircraftFee: 10
  });

  const fetchManeuvers = React.useCallback(() => {
    if (!currentUser?.sessionId) {
      setManeuvers([]);
      return;
    }
    const headers = { 'session-id': currentUser.sessionId };
    fetch(`${API_BASE_URL}/maneuver/all`, { headers })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        return response.json();
      })
      .then(data => {
        setManeuvers(Array.isArray(data) ? data : []);
      })
      .catch(error => {
        setManeuvers([]);
      });
  }, [currentUser?.sessionId]);

  useEffect(() => {
    fetchManeuvers();
  }, [fetchManeuvers]);

  useEffect(() => {
    if (!currentUser?.sessionId) return;
    const headers = { 'session-id': currentUser.sessionId };
    fetch(`${API_BASE_URL}/settings/all`, { headers })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        return response.json();
      })
      .then(data => {
        const fetched = Array.isArray(data) ? (data[0] || null) : data;
        if (fetched && typeof fetched === 'object') {
          setSettings(prev => ({
            ...prev,
            ...fetched,
            simRate: fetched.simRate != null ? fetched.simRate : prev.simRate,
            instructionOwnerAircraftFee: fetched.instructionOwnerAircraftFee != null ? fetched.instructionOwnerAircraftFee : prev.instructionOwnerAircraftFee
          }));
        }
      })
        .catch(error => {
        });
  }, [currentUser?.sessionId]);

  const [reservations, setReservations] = useState([]);

  const fetchReservations = React.useCallback(async (startDate, endDate) => {
    if (!currentUser?.sessionId) {
      return;
    }

    const normalizeReservations = (data) => {
      if (Array.isArray(data)) {
        return data;
      }

      if (data && typeof data === 'object') {
        if (Array.isArray(data.reservations)) {
          return data.reservations;
        }
        if (Array.isArray(data.data)) {
          return data.data;
        }
        if (Array.isArray(data.items)) {
          return data.items;
        }

        const firstArrayValue = Object.values(data).find((value) => Array.isArray(value));
        if (Array.isArray(firstArrayValue)) {
          return firstArrayValue;
        }
      }

      return [];
    };
    const headers = { 'session-id': currentUser.sessionId };

    try {
      const apiUrl = `${API_BASE_URL}/reservation/list/${startDate}/${endDate}`;
      const response = await fetch(apiUrl, { headers });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      const primaryReservations = normalizeReservations(data);

      // Fallback: some backend deployments return empty arrays for multi-day range queries.
      if (primaryReservations.length === 0 && startDate !== endDate) {
        const start = new Date(`${startDate}T00:00:00`);
        const end = new Date(`${endDate}T00:00:00`);
        if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime()) && start <= end) {
          const mergedById = new Map();
          const cursor = new Date(start);

          while (cursor <= end) {
            const dayStr = cursor.toISOString().split('T')[0];
            const dayResponse = await fetch(`${API_BASE_URL}/reservation/list/${dayStr}/${dayStr}`, { headers });
            if (dayResponse.ok) {
              const dayData = await dayResponse.json();
              normalizeReservations(dayData).forEach((reservation) => {
                const key = reservation?.id != null ? reservation.id : `${reservation?.startDate || reservation?.start_date || dayStr}-${reservation?.startTime || ''}-${reservation?.userIdClient || reservation?.userId || ''}`;
                mergedById.set(key, reservation);
              });
            }
            cursor.setDate(cursor.getDate() + 1);
          }

          setReservations(Array.from(mergedById.values()));
          return;
        }
      }

      setReservations(primaryReservations);
    } catch (error) {
      setReservations([]);
    }
  }, [currentUser?.sessionId]);

  const addInstructor = (instructor) => {
    setInstructors([...instructors, { ...instructor, id: Date.now() }]);
  };

  const updateInstructor = (id, updatedInstructor) => {
    setInstructors(instructors.map(i => i.id === id ? { ...updatedInstructor, id } : i));
  };

  const deleteInstructor = (id) => {
    setInstructors(instructors.filter(i => i.id !== id));
  };

  const addPerson = (person) => {
    setPeople([...people, { ...person, id: Date.now() }]);
  };

  const updatePerson = (id, updatedPerson) => {
    setPeople(people.map(p => p.id === id ? { ...updatedPerson, id } : p));
  };

  const deletePerson = (id) => {
    setPeople(people.filter(p => p.id !== id));
  };

  const addAircraft = (aircraft) => {
    setAircraft(prev => [...prev, { ...aircraft, id: Date.now() }]);
  };

  const updateAircraft = (id, updatedAircraft) => {
    setAircraft(aircraft.map(a => a.id === id ? { ...updatedAircraft, id } : a));
  };

  const deleteAircraft = (id) => {
    setAircraft(aircraft.filter(a => a.id !== id));
  };

  const addReservation = (reservation) => {
    setReservations([...reservations, { ...reservation, id: Date.now() }]);
  };

  const fetchInstructors = React.useCallback(() => {
    if (!currentUser?.sessionId) return;
    const headers = currentUser?.sessionId ? { 'session-id': currentUser.sessionId } : {};
    fetch(`${API_BASE_URL}/instructors`, { headers })
      .then(async response => {
        const data = await response.json();
        if (!response.ok) throw new Error(data?.error || `HTTP ${response.status}: ${response.statusText}`);
        if (!Array.isArray(data)) throw new Error('Invalid instructor list response');
        return data;
      })
      .then(data => {
        setInstructors(data);
        setInstructorError('');
      })
      .catch(error => { setInstructorError(error.message); });
  }, [currentUser?.sessionId]);

  const fetchAircraft = React.useCallback(() => {
    const headers = currentUser?.sessionId ? { 'session-id': currentUser.sessionId } : {};
    fetch(`${API_BASE_URL}/aircraft/all`, { headers })
      .then(response => response.json())
      .then(data => {
        const normalizedAircraft = Array.isArray(data)
          ? data.map(item => ({
              ...item,
              sortOrder: item.sortOrder ?? item.sort_order ?? item.sortorder ?? ''
            })).sort((a, b) => {
              const aOrder = Number(a.sortOrder);
              const bOrder = Number(b.sortOrder);
              const aValue = Number.isFinite(aOrder) ? aOrder : Number.MAX_SAFE_INTEGER;
              const bValue = Number.isFinite(bOrder) ? bOrder : Number.MAX_SAFE_INTEGER;
              if (aValue !== bValue) return aValue - bValue;
              const aName = `${a.make || a.manufacturer || ''} ${a.model || ''} ${a.tailNumber || a.registration || ''}`.toLowerCase();
              const bName = `${b.make || b.manufacturer || ''} ${b.model || ''} ${b.tailNumber || b.registration || ''}`.toLowerCase();
              return aName.localeCompare(bName);
            })
          : [];
        setAircraft(normalizedAircraft);
      })
      .catch(error => {});
  }, [currentUser?.sessionId]);

  const fetchUsers = React.useCallback(() => {
    if (!currentUser?.sessionId) {
      return;
    }
    const headers = { 'session-id': currentUser.sessionId };
    fetch(`${API_BASE_URL}/user/all`, { headers })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        return response.json();
      })
      .then(data => setPeople(Array.isArray(data) ? data : []))
      .catch(error => {
        setPeople([]);
      });
  }, [currentUser?.sessionId]);

  const fetchRoles = React.useCallback(() => {
    const headers = currentUser?.sessionId ? { 'session-id': currentUser.sessionId } : {};
    fetch(`${API_BASE_URL}/role/list`, { headers })
      .then(response => response.json())
      .then(data => {
        setRoles(data);
      })
      .catch(error => {});
  }, [currentUser?.sessionId]);

  const logout = React.useCallback(() => {
    setCurrentUser(null);
    setIsPopupQuestionLoading(false);
  }, [setCurrentUser]);

  React.useLayoutEffect(() => observeSessionFailures(token => {
    if (sessionRef.current?.user?.sessionId === token) logout();
  }), [logout]);

  useEffect(() => {
    if (!session?.expiresAt) return undefined;
    const checkExpiry = () => {
      if (sessionRef.current?.expiresAt <= Date.now()) logout();
    };
    const timer = window.setTimeout(checkExpiry, Math.max(0, session.expiresAt - Date.now()));
    window.addEventListener('focus', checkExpiry);
    document.addEventListener('visibilitychange', checkExpiry);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('focus', checkExpiry);
      document.removeEventListener('visibilitychange', checkExpiry);
    };
  }, [session?.expiresAt, logout]);

  useEffect(() => {
    const restoreSession = event => {
      if (event.key !== null && event.key !== 'sessionExpiresAt') return;
      const restored = loadStoredSession();
      sessionRef.current = restored;
      setSession(restored);
    };
    window.addEventListener('storage', restoreSession);
    return () => window.removeEventListener('storage', restoreSession);
  }, []);

  const value = {
    locationWorkSchedules, locationWorkSchedulesError, loadingLocationWorkSchedules, fetchLocationWorkSchedules, updateLocationWorkSchedules,
    locationAircraftSchedules, locationAircraftSchedulesError, loadingLocationAircraftSchedules, fetchLocationAircraftSchedules, updateLocationAircraftSchedules,
    reservationTypes, fetchReservationTypes, reservationTypesError, loadingReservationTypes,
    locations, hasMultipleLocations: locations.length > 1, selectedLocationId, setSelectedLocationId, fetchLocations, locationError, locationResources, refreshLocationResources,
    instructors, instructorError, people, aircraft, ownedAircraft, reservations, roles, maneuvers, currentUser, currentOrg, isLoading, isPopupQuestionLoading, settings,
    addInstructor, updateInstructor, deleteInstructor,
    addPerson, updatePerson, deletePerson,
    addAircraft, updateAircraft, deleteAircraft,
    addReservation, fetchInstructors, fetchAircraft, fetchUsers, fetchReservations, fetchRoles, fetchManeuvers, setCurrentUser, setIsLoading, setIsPopupQuestionLoading, setSettings, logout
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};